import {createReducer, on} from "@ngrx/store";
import {INITIAL_FAVORITE_STATE, FavoriteState} from "./favorite.state";
import {FavoriteActions} from "./favorite.actions";
import {Favorite} from "../models/favorite.model";

export const favoriteReducer = createReducer<FavoriteState>(
    INITIAL_FAVORITE_STATE,

    on(FavoriteActions.load, (state) => ({
        ...state,
        list: {...state.list, status: "LOADING", errorMessage: ""},
    })),
    on(FavoriteActions.loadSuccess, (state, {favorites}) => ({
        ...state,
        list: {data: sortBySortOrder(favorites), status: "LOADED", errorMessage: ""},
    })),
    on(FavoriteActions.loadError, (state, {errorMsg}) => ({
        ...state,
        list: {...state.list, status: "ERROR", errorMessage: errorMsg},
    })),

    // Create is upsert-by-url on the backend: replace an existing row (same id or url) or append.
    on(FavoriteActions.createSuccess, (state, {favorite}) => ({
        ...state,
        list: {...state.list, data: upsert(state.list.data, favorite), errorMessage: ""},
    })),
    on(FavoriteActions.createError, (state, {errorMsg}) => ({
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),

    // Optimistic update — patch the visible row immediately; updateError repairs via reload effect.
    on(FavoriteActions.update, (state, {id, request}) => ({
        ...state,
        list: {
            ...state.list,
            data: state.list.data.map((f) =>
                f.id === id ? {...f, label: request.label, color: request.color, note: request.note} : f
            ),
        },
    })),
    on(FavoriteActions.updateSuccess, (state, {favorite}) => ({
        ...state,
        list: {...state.list, data: upsert(state.list.data, favorite), errorMessage: ""},
    })),
    on(FavoriteActions.updateError, (state, {errorMsg}) => ({
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),

    // Optimistic remove — deleteError repairs via reload effect.
    on(FavoriteActions.delete, (state, {id}) => ({
        ...state,
        list: {...state.list, data: state.list.data.filter((f) => f.id !== id)},
    })),
    on(FavoriteActions.deleteError, (state, {errorMsg}) => ({
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),

    // Optimistic reorder — reorderError repairs via reload effect.
    on(FavoriteActions.reorder, (state, {favoriteIds}) => ({
        ...state,
        list: {...state.list, data: applyOrder(state.list.data, favoriteIds)},
    })),
    on(FavoriteActions.reorderError, (state, {errorMsg}) => ({
        ...state,
        list: {...state.list, errorMessage: errorMsg},
    })),
);

function sortBySortOrder(favorites: Favorite[]): Favorite[] {
    return [...favorites].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

/**
 * Insert/replace a favorite, keyed on id OR url (url is the backend's upsert key). Any existing row
 * colliding on either key is dropped before the incoming favorite is added, so even a stale list that
 * briefly held the id and the url on two different rows collapses to a single row. Stays sortOrder-sorted.
 */
function upsert(data: Favorite[], favorite: Favorite): Favorite[] {
    const withoutCollisions = data.filter((f) => f.id !== favorite.id && f.url !== favorite.url);
    return sortBySortOrder([...withoutCollisions, favorite]);
}

/**
 * Re-orders `data` to match the sequence of `favoriteIds`, assigning each a fresh contiguous
 * sortOrder. Ids not present in the list are ignored; favorites omitted from `favoriteIds` keep
 * their relative order and are appended after the supplied ones (mirrors the backend contract).
 */
function applyOrder(data: Favorite[], favoriteIds: number[]): Favorite[] {
    const byId = new Map(data.map((f) => [f.id, f]));
    const ordered: Favorite[] = [];
    favoriteIds.forEach((id) => {
        const found = byId.get(id);
        if (found) {
            ordered.push(found);
            byId.delete(id);
        }
    });
    // Remaining favorites keep their previous relative order.
    data.forEach((f) => {
        if (byId.has(f.id)) {
            ordered.push(f);
        }
    });
    return ordered.map((f, index) => ({...f, sortOrder: index}));
}
